import { useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { useAuth } from '../lib/AuthContext';
import { tokens } from '../theme';

const NAV = [
  { to: '/apps', label: 'Applications' },
  { to: '/status', label: 'Status' },
  { to: '/about', label: 'About us' },
];

const navLinkSx = {
  color: tokens.text,
  textDecoration: 'none',
  fontWeight: 500,
  py: 1.25,
  px: 0.5,
  borderBottom: '2px solid transparent',
  '&:hover': { color: 'primary.main', borderBottomColor: 'primary.main' },
  '&.active': { color: 'primary.main', borderBottomColor: 'primary.main' },
} as const;

function TrustStrip() {
  const [open, setOpen] = useState(false);
  const host = typeof window !== 'undefined' ? window.location.host : '';
  return (
    <Box sx={{ bgcolor: tokens.strip, borderBottom: `1px solid ${tokens.divider}`, fontSize: 13 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        flexWrap="wrap"
        sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, minHeight: 36 }}
      >
        <LockOutlinedIcon sx={{ fontSize: 14, color: '#344054' }} aria-hidden />
        <Typography fontSize={13} color="#344054">
          An Advanced Substrate Technologies internal site
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="trust-details"
          sx={{
            background: 'none',
            border: 0,
            p: 0,
            py: 1,
            font: 'inherit',
            fontSize: 13,
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          How to identify
        </Box>
      </Stack>
      <Collapse in={open} id="trust-details">
        <Box
          sx={{
            maxWidth: 1240,
            mx: 'auto',
            px: { xs: 2, sm: 4 },
            pt: 1,
            pb: 2.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            color: '#344054',
            fontSize: 14,
          }}
        >
          <Typography fontSize={14}>
            <strong style={{ color: tokens.ink }}>This portal lives at {host}.</strong> If the address in
            your browser looks different, don’t sign in.
          </Typography>
          <Typography fontSize={14}>
            <strong style={{ color: tokens.ink }}>Secure sites use HTTPS.</strong> Look for the lock before
            entering your AST credentials.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

function Footer() {
  const { user } = useAuth();
  const linkSx = { color: tokens.footerText, fontWeight: 400, '&:hover': { color: '#fff' } };
  return (
    <Box component="footer" sx={{ bgcolor: tokens.footer, color: '#fff' }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 6, md: 9 }, pb: 5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={6} justifyContent="space-between">
          <Stack spacing={1.5} sx={{ maxWidth: 320 }}>
            <Typography fontSize={22} fontWeight={700} color="#fff">
              AST IT Lab
            </Typography>
            <Typography color={tokens.footerText}>
              Internal software for Advanced Substrate Technologies.
            </Typography>
          </Stack>
          <Box
            component="nav"
            aria-label="Footer"
            sx={{
              flex: 1,
              maxWidth: 640,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
              gap: 4,
            }}
          >
            <Stack spacing={1.25}>
              <Typography fontWeight={600} color="#fff">
                Portal
              </Typography>
              <Link component={RouterLink} to="/apps" sx={linkSx}>
                Applications
              </Link>
              <Link component={RouterLink} to="/request" sx={linkSx}>
                Request an app
              </Link>
              <Link component={RouterLink} to="/status" sx={linkSx}>
                Status
              </Link>
            </Stack>
            <Stack spacing={1.25}>
              <Typography fontWeight={600} color="#fff">
                IT Lab
              </Typography>
              <Link component={RouterLink} to="/about" sx={linkSx}>
                About us
              </Link>
              {user?.role === 'admin' && (
                <Link component={RouterLink} to="/admin" sx={linkSx}>
                  Admin
                </Link>
              )}
            </Stack>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          gap={1.5}
          sx={{ mt: 6, pt: 3, borderTop: '1px solid #24364A', fontSize: 14, color: tokens.footerText }}
        >
          <Typography fontSize={14} color={tokens.footerText}>
            © {new Date().getFullYear()} Advanced Substrate Technologies Pte. Ltd.
          </Typography>
          <Typography fontSize={14} color={tokens.footerText}>
            v{__APP_VERSION__}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export function AppShell() {
  const { user, guest, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawer, setDrawer] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const links = [...NAV, ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : [])];

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" bgcolor="#fff">
      <TrustStrip />
      <Box component="header" sx={{ borderBottom: `1px solid ${tokens.divider}`, bgcolor: '#fff' }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={{ xs: 1.5, md: 4 }}
          sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, py: 1.75 }}
        >
          {mobile && (
            <IconButton edge="start" onClick={() => setDrawer(true)} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          )}
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            spacing={1.75}
            alignItems="center"
            sx={{ textDecoration: 'none', color: tokens.ink }}
          >
            <Box component="img" src="/ast-logo.png" alt="AST" sx={{ height: 32, width: 'auto' }} />
            <Typography fontWeight={700} fontSize={17} letterSpacing="-0.01em" color={tokens.ink}>
              IT Lab
            </Typography>
          </Stack>

          <Box flex={1} />

          {!mobile && (
            <Stack component="nav" aria-label="Primary" direction="row" gap={3.5}>
              {links.map((l) => (
                <Box key={l.to} component={NavLink} to={l.to} sx={navLinkSx}>
                  {l.label}
                </Box>
              ))}
            </Stack>
          )}

          {!mobile && (
            <Button component={RouterLink} to="/request" variant="contained">
              Request an app
            </Button>
          )}

          {guest && !user ? (
            <Button component={RouterLink} to="/login" variant="outlined">
              Sign in
            </Button>
          ) : (
            <>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label="Account menu"
            sx={{ border: `1px solid ${tokens.divider}`, width: 44, height: 44 }}
          >
            <AccountCircleOutlinedIcon />
          </IconButton>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <ListItemText
                primary={user?.name || user?.email}
                secondary={`${user?.email ?? ''}${user?.department ? ` · ${user.department}` : ''}`}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                setAnchor(null);
                await logout();
                navigate('/');
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
            </>
          )}
        </Stack>
      </Box>

      <Drawer open={drawer} onClose={() => setDrawer(false)}>
        <Stack component="nav" aria-label="Primary" width={280} p={3} spacing={1}>
          <Box component={NavLink} to="/" end onClick={() => setDrawer(false)} sx={navLinkSx}>
            Home
          </Box>
          {links.map((l) => (
            <Box key={l.to} component={NavLink} to={l.to} onClick={() => setDrawer(false)} sx={navLinkSx}>
              {l.label}
            </Box>
          ))}
          <Box pt={2}>
            <Button component={RouterLink} to="/request" variant="contained" fullWidth onClick={() => setDrawer(false)}>
              Request an app
            </Button>
          </Box>
        </Stack>
      </Drawer>

      <Box component="main" flex={1}>
        <Outlet />
      </Box>

      <Footer />
    </Box>
  );
}
